/*
 * Copyright (c) 2019 - Convergence Labs, Inc.
 *
 * This file is part of the Convergence Server, which is released under
 * the terms of the GNU General Public License version 3 (GPLv3). A copy
 * of the GPLv3 should have been provided along with this file, typically
 * located in the "LICENSE" file, which is part of this source code package.
 * Alternatively, see <https://www.gnu.org/licenses/gpl-3.0.html> for the
 * full text of the GPLv3 license, if it was not provided.
 */

import React, {ReactNode} from "react";
import {Page} from "../../../../components";
import {Card, Switch, Table} from "antd";
import {RouteComponentProps} from "react-router";
import {formatDomainUserId, longDateTime, shortDateTime} from "../../../../utils/format-utils";
import {DomainSession} from "../../../../models/domain/DomainSession";
import {DomainSessionFilter, DomainSessionService} from "../../../../services/domain/DomainSessionService";
import {makeCancelable, PromiseSubscription} from "../../../../utils/make-cancelable";
import {DomainId} from "../../../../models/DomainId";
import {DomainUserId} from "@convergence/convergence";
import {SessionTableControls, SessionTableFilters} from "../../../../components/domain/session/SessionTableControls";
import {SERVICES} from "../../../../services/ServiceConstants";
import {injectAs} from "../../../../utils/mobx-utils";
import {CardTitleToolbar} from "../../../../components/common/CardTitleToolbar";
import styles from "./styles.module.css";
import {CloudOutlined} from "@ant-design/icons";

export interface DomainSessionsProps extends RouteComponentProps {
  domainId: DomainId;
}

interface InjectedProps extends DomainSessionsProps {
  domainSessionService: DomainSessionService;
}

export interface DomainSessionsState {
  loading: boolean;
  sessions: DomainSession[] | null;
  filter: DomainSessionFilter;
}

class DomainSessionsComponent extends React.Component<InjectedProps, DomainSessionsState> {
  private readonly _breadcrumbs = [{title: "Sessions"}];
  private readonly _sessionColumns: any[];
  private _sessionsSubscription: PromiseSubscription | null;

  constructor(props: InjectedProps) {
    super(props);

    this.state = {
      sessions: [],
      loading: false,
      filter: {
        excludeDisconnected: true,
        sessionType: "ExcludeConvergence"
      }
    };

    this._sessionColumns = [{
      title: 'Session Id',
      dataIndex: 'id',
      sorter: true,
    }, {
      title: 'User',
      dataIndex: 'userId',
      sorter: true,
      render: (val: DomainUserId, _: DomainSession) => formatDomainUserId(val),
    }, {
      title: 'Auth Method',
      dataIndex: 'authMethod'
    }, {
      title: 'Connected At',
      dataIndex: 'connected',
      render: (val: Date, _: DomainSession) => val ? shortDateTime(val) : "",
      sorter: (a: DomainSession, b: DomainSession) => a.connected.getTime() - b.connected.getTime()
    }, {
      title: 'Disconnected At',
      dataIndex: 'disconnected',
      render: (val: Date, _: DomainSession) => val ? shortDateTime(val) : ""
    }, {
      title: 'Remote Host',
      dataIndex: 'remoteHost',
    }];

    this._sessionsSubscription = null;
    this._loadSessions();
  }

  public render(): ReactNode {
    if (this.state.sessions !== null) {
      return (
        <Page breadcrumbs={this._breadcrumbs}>
          <Card title={this._renderToolbar()}>
            <SessionTableControls onFilter={this._onFilter}/>
            <Table columns={this._sessionColumns}
                   size="middle"
                   rowKey="id"
                   bordered={true}
                   loading={this.state.loading}
                   dataSource={this.state.sessions}
                   expandedRowRender={this._expander}
            />
          </Card>
        </Page>
      );
    } else {
      return null;
    }
  }

  private _renderToolbar = () => {
    return <CardTitleToolbar icon={<CloudOutlined />} title="Sessions">
      <div className={styles.titleTools}>
        <span>Disconnected Sessions:</span><Switch onChange={this._onChangeDisconnected}/>
        <span>Convergence Users:</span><Switch onChange={this._onChangeConvergenceUsers}/>
      </div>
    </CardTitleToolbar>

  }

  private _expander = (session: DomainSession) => {
    return (
      <div className={styles.expander}>
        <table>
          <tbody>
          <tr>
            <td>Session Id:</td>
            <td>{session.id}</td>
          </tr>
          <tr>
            <td>Username:</td>
            <td>{session.userId.username}</td>
          </tr>
          <tr>
            <td>User Type:</td>
            <td>{session.userId.userType}</td>
          </tr>
          <tr>
            <td>Auth Method:</td>
            <td>{session.authMethod}</td>
          </tr>
          <tr>
            <td>Connected At:</td>
            <td>{longDateTime(session.connected)}</td>
          </tr>
          <tr>
            <td>Disconnected At:</td>
            <td>{session.disconnected ? longDateTime(session.disconnected) : ""}</td>
          </tr>
          <tr>
            <td>Remote Host:</td>
            <td>{session.remoteHost}</td>
          </tr>
          <tr>
            <td>Client:</td>
            <td>{session.client}</td>
          </tr>
          <tr>
            <td>Client Version:</td>
            <td>{session.clientVersion}</td>
          </tr>
          <tr>
            <td>Client Meta Data:</td>
            <td>{session.clientMetaData}</td>
          </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private _onChangeDisconnected = (showDisconnected: any) => {
    const filter: DomainSessionFilter = {...this.state.filter, excludeDisconnected: !showDisconnected}
    this.setState({filter}, () => this._loadSessions());
  }

  private _onChangeConvergenceUsers = (showConvergenceUsers: boolean) => {
    const sessionType = showConvergenceUsers ? "all" : "ExcludeConvergence";
    const filter: DomainSessionFilter = {...this.state.filter, sessionType}
    this.setState({filter}, () => this._loadSessions());
  }

  private _onFilter = (filters: SessionTableFilters) => {
    const filter: DomainSessionFilter = {...this.state.filter, ...filters};
    this.setState({filter}, () => this._loadSessions());
  }

  private _loadSessions = () => {
    const {promise, subscription} = makeCancelable(
      this.props.domainSessionService.getSessions(this.props.domainId, this.state.filter)
    );
    this._sessionsSubscription = subscription;
    promise.then(sessions => {
      this._sessionsSubscription = null;
      this.setState({sessions: sessions.data});
    }).catch(err => {
      console.error(err);
      this._sessionsSubscription = null;
      this.setState({sessions: null});
    });
  }
}

const injections = [SERVICES.DOMAIN_SESSION_SERVICE];
export const DomainSessions = injectAs<DomainSessionsProps>(injections, DomainSessionsComponent);
